import React from 'react';
import s from './s.module.less';
import dayjs from 'utils/dayjs';
import UserAddIcon from 'assets/prospects/user.svg';
import { getTimeStr } from 'utils/calendar';
import AddAndUpdateProspects from 'components/AddAndUpdateProspects';
import {
    EStatus,
    EProspectType,
    EOnBoardingStatus,
    Prospect,
    TProspectInsurance,
    MaritalStatusValue,
} from 'types/common';
import DataUpdatedFlagStore from 'store/DataUpdatedFlag';
import {
    getAgeFromBirthday,
    displayItem,
    isOpPortal,
    getIntakeFormUrlByTextVisitId,
    getIntakeFormUrlByAppointmentId,
    getUrlParam,
} from 'utils/common';
import CheckCircleIcon from 'assets/common/checkCircle.svg';
import UnCircleIcon from 'assets/common/unCheck.svg';
import { DISABLE_CHARGE_PATIENTS_POPUP, EMPTY_PLACEHOLDER_WITH_DASH } from 'constants/common';
import InboxNotes from 'components/InboxNotes';
// eslint-disable-next-line import/no-cycle
import AppointmentList from 'components/AppointmentList';
import { Button, message, Modal, Popover, Tabs, Tooltip, Dropdown } from 'antd';
import { CloseOutlined, DownOutlined } from '@ant-design/icons';
import ChargeModal from './components/ChargeModal';
import InvoiceModal from './components/InvoiceModal';
import { IBillingPolicy } from 'types/paymentSetup';
import { IConnectAccountInfoResponse } from 'hooks/useGetConnectAccountInfo';
import { handleDownloadPhoto } from 'utils/file';
import AlertChargeSuccess from './components/AlertChargeSuccess';
import InboxForms from 'components/InboxForms';
import PrescriptionsAndPharmacy from '../PrescriptionsAndPharmacy';
import Billings from './components/Billings';
import PatientHealthInfoUpdateDialog, { PatientUpdateType } from 'components/PatientUpdate/HealthInfoDialog';
import PencilIcon from 'assets/common/pencil.svg';
import PharmacySelectDialog from 'components/PatientUpdate/PharmacySelectDialog';
import UpdatedRecordPopover from 'components/PatientUpdate/UpdatedRecordPopover';
import { getProviderPrivateFileSigned } from 'api/common';
import { getPrivateFilePreview } from 'api/operation';
import { EIntakeFormStatus } from 'types/calendar';
import { TextVisitCase, TFormAnswer } from 'types/textVisit';
import SpecificIntakeFormAssessmentBlock from 'components/IntakeFormDrawer/component/IntakeFormContent/components/SpecificIntakeFormAssessmentBlock';
import { collectSpecificIntakeAnswers, parseSpecificIntakeFormGroups } from 'utils/intakeAssessmentScores';
import { sendMessage } from 'api/inbox';
import { getUserId } from 'utils/localstore';
import { getPrimaryInsurances, getSecondaryInsurances } from 'utils/clientEligibility';
import useOpsGetProviderForPatient from 'hooks/useOpsGetProviderForPatient';
import { IVideoTreatmentDetail } from 'types/treatment';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { AddClientTagsModal, ClientTags } from 'components/ClientTags';
import type { ProspectTagVo } from 'types/prospectTag';
import {
    addProspectTagsIncremental,
    fetchProspectTags,
    removeProspectTagByName,
    reorderProspectTagsByNames,
} from 'utils/prospectTags';
import EligibilityStatusTag from 'components/EligibilityStatusTag';
import EligibilityModule from '../EligibilityModule';
import useClientEligibilityCheck from '../EligibilityModule/hooks/useClientEligibilityCheck';
import { CHOOSE_PROVIDER_CARD_TIP, HISTORY_COPY } from '../EligibilityModule/copy';
import BillingForVisit from './components/BillingForVisit';
import InsurancePopup from './components/InsurancePopup';
import EditInsuranceModal from './components/EditInsuranceModal';

type Props = {
    showMoreInfo?: boolean;
    data?: Prospect;
    onUpdate: () => void;
    loading?: boolean;
    smallSize?: boolean;
    forceResetInfo?: {};
    billingPolicy?: IBillingPolicy[];
    connectAccountInfo?: IConnectAccountInfoResponse;
    hasPlan?: boolean;
    isSimpleType?: boolean;
    hideCharge?: boolean;
    textVisitItem?: TextVisitCase;
    videoVisitItem?: IVideoTreatmentDetail;
    showIntakeTab?: boolean;
    isOps?: boolean;
    isInTextVisitPop?: boolean;
    isGroupPractice?: boolean;
    // eligibility 模块只在 client detail 页显示；其余 5 处复用点（Inbox/Meeting/TextVisit 等）不传即关
    showEligibility?: boolean;
    showBillingForVisit?: boolean;
};

const initPatientUpdateDialog = {
    open: false,
    type: PatientUpdateType.CurrentMedications,
};

const initPharmacyUpdateDialog = {
    open: false,
};

const ProsepectInfo = (props: Props) => {
    const {
        data,
        onUpdate,
        loading = false,
        hideCharge,
        smallSize,
        showMoreInfo,
        forceResetInfo,
        billingPolicy,
        connectAccountInfo,
        hasPlan,
        isSimpleType,
        textVisitItem,
        videoVisitItem,
        showIntakeTab,
        isOps,
        isInTextVisitPop,
        isGroupPractice,
        showEligibility,
        showBillingForVisit,
    } = props;
    const [activeKey, setActiveKey] = React.useState<string>('1');
    // ?subTab=2 直达 Insurance & payment（To-do "Insurance to re-check" 的 Check eligibility 跳转入口）
    const initialSubTab = getUrlParam('subTab') === '2' ? '2' : '1';
    // 同一个入口带来的 appointmentId：这一次 Run check 走 per-appointment 的 recheck 接口
    const recheckAppointmentId = Number(getUrlParam('recheckAppointmentId')) || undefined;
    const [editType, setEditType] = React.useState<1 | 2 | 3>(initialSubTab === '2' ? 2 : 1);
    const [subTabKey, setSubTabKey] = React.useState<string>(initialSubTab);
    const [moreInfoTab, setMoreInfoTab] = React.useState<string | number>('1');
    const [showEdit, setShowEdit] = React.useState(false);
    const [, setDataUpdatedFlag] = DataUpdatedFlagStore.useStore();
    const name = `${data?.firstName || ''} ${data?.lastName || ''}`;
    const [viewImgUrl, setViewImgUrl] = React.useState<string | undefined>();
    const [idFrontUrl, setIdFrontUrl] = React.useState<string | undefined>();
    const [idBackUrl, setIdBackUrl] = React.useState<string | undefined>();
    const [showChargeClientPopover, setShowChargeClientPopover] = React.useState(true);
    const [showChargeClientModal, setShowChargeClientModal] = React.useState(false);
    const [showSendInvoiceModal, setShowSendInvoiceModal] = React.useState(false);
    const [showChargeSuccessTip, setShowChargeSuccessTip] = React.useState(false);
    const [chargeSuccessInfo, setChargeSuccessInfo] = React.useState<{ name: string; amount: number } | null>(null);
    const [patientUpdateDialog, setPatientUpdateDialog] = React.useState({ ...initPatientUpdateDialog });
    const [pharmacyUpdateDialog, setPharmacyUpdateDialog] = React.useState({ ...initPharmacyUpdateDialog });
    const isSample = data?.isSample;
    const creditCardOnFile = data?.creditCardOnFile;
    const [showInsurancePopup, setShowInsurancePopup] = React.useState<'PRIMARY' | 'SECONDARY' | false>(false);
    const [showEditInsurance, setShowEditInsurance] = React.useState(false);
    const [sendingIntakeReminder, setSendingIntakeReminder] = React.useState<boolean>(false);
    const [allIntakeAnswers, setAllIntakeAnswers] = React.useState<TFormAnswer[]>([]);
    const [providerList] = useOpsGetProviderForPatient(isOps ? data?.klarityUserId : undefined);
    const [homeInfo] = useProviderHomeInfo();
    const isPPAProvider = homeInfo?.ppaProvider;
    const showRelateProvider = isOps && providerList && providerList.length > 0;
    const providersText = providerList?.map((item) => item.providerName).join(', ');
    let patientPortal = 'Not sent';
    if (data?.onBoardingStatus === EOnBoardingStatus.SENT) {
        patientPortal = 'Sent';
    } else if (data?.onBoardingStatus === EOnBoardingStatus.COMPLETE) {
        patientPortal = 'Signed up';
    } else if (data?.onBoardingStatus === EOnBoardingStatus.IN_PROGRESS) {
        patientPortal = 'In progress';
    }

    const cNumber = data?.cardNum || data?.patientCardNum || '';
    const cExp = data?.cardExpirationDate || data?.patientCardExpirationDate || '';

    const pharmacy = data?.pharmacy?.[0];
    // eligibility 数据 + check 状态机：未开启（复用点/ops）时传 undefined，hook 全 no-op 不发任何请求。
    // onUpdate 作为 check 后的重拉：结果落回后端下发的 (provider, sibling)，切 provider 不会丢
    const eligApi = useClientEligibilityCheck(
        showEligibility && !isOps ? data : undefined,
        isGroupPractice,
        onUpdate,
        recheckAppointmentId,
    );
    // practice 视图：保险资料区跟着选中的 provider 走——identity 下每位 provider 名下是各自的
    // 保险行（id 不同），屏幕上这张卡必须就是 check 发出去的那条 insuranceId
    const shownInsurances = eligApi.activeInsurances || data?.insurances;
    // practice 视图下还没挑 provider：卡片请求没有发件身份，也不知道该写进谁名下的记录
    const needsProviderPick = eligApi.providers.length > 0 && !eligApi.selectedProviderId;
    // default 在前（set-default 只翻 isDefault 不动 sort），与 eligibility check 默认发的那张是同一张
    const primaryInsurances = React.useMemo(() => getPrimaryInsurances(shownInsurances), [shownInsurances]);
    const secondaryInsurances = React.useMemo(() => getSecondaryInsurances(shownInsurances), [shownInsurances]);
    const insurance1: TProspectInsurance | undefined = primaryInsurances[0];
    const insurance2: TProspectInsurance | undefined = secondaryInsurances[0];
    const secondInsurance = !!insurance2;
    const morePrimaryCount = primaryInsurances.length > 1 ? primaryInsurances.length - 1 : 0;
    const moreSecondaryCount = secondaryInsurances.length > 1 ? secondaryInsurances.length - 1 : 0;
    const intakeFormStatus = textVisitItem?.intakeFormStatus || videoVisitItem?.intakeFormStatus;
    let displayIntakeTab = showIntakeTab && intakeFormStatus !== EIntakeFormStatus.NONE;
    const intakeFormCompleted = intakeFormStatus === EIntakeFormStatus.COMPLETED;

    const [tagList, setTagList] = React.useState<ProspectTagVo[]>([]);
    const [tagListLoading, setTagListLoading] = React.useState(false);
    const [showAddTags, setShowAddTags] = React.useState(false);

    const normalizeTag = React.useCallback((t: unknown): string => {
        if (t == null) return '';
        if (typeof t === 'string') return t.trim();
        if (typeof t === 'object') {
            const maybe = t as { tagName?: unknown; name?: unknown; label?: unknown };
            const candidate = maybe.tagName ?? maybe.name ?? maybe.label;
            if (typeof candidate === 'string') return candidate.trim();
        }
        return '';
    }, []);

    const normalizedTags = React.useMemo(() => {
        const raw = (data as unknown as { tags?: unknown })?.tags;
        if (!Array.isArray(raw)) return [];
        return raw.map(normalizeTag).filter(Boolean);
    }, [data, normalizeTag]);

    const tagClientId = isGroupPractice ? data?.identityId : data?.id;
    const refreshProspectTagList = React.useCallback(async () => {
        if (!tagClientId) return;
        setTagListLoading(true);
        try {
            const next = await fetchProspectTags(tagClientId, isGroupPractice);
            setTagList(next);
        } finally {
            setTagListLoading(false);
        }
    }, [tagClientId, isGroupPractice]);

    React.useEffect(() => {
        // Keep the UI consistent with the list page: tags are managed via /prospect/tag/* APIs.
        // Fallback to `data.tags` when tag list API hasn't loaded yet.
        refreshProspectTagList();
    }, [refreshProspectTagList]);

    const normalizeTags = React.useCallback(
        (tags: unknown): string[] => {
            if (!Array.isArray(tags)) return [];
            return tags.map(normalizeTag).filter(Boolean);
        },
        [normalizeTag],
    );

    const currentTagNames = React.useMemo(() => {
        if (tagList.length > 0) return tagList.map((x) => x.tagName).filter(Boolean);
        return normalizedTags;
    }, [tagList, normalizedTags]);

    const handleAddTags = React.useCallback(
        async (next: string[]) => {
            if (!tagClientId) return;
            const { added } = await addProspectTagsIncremental({
                prospectId: tagClientId,
                existingTagNames: currentTagNames,
                nextTagNames: normalizeTags(next),
                isPracticeView: isGroupPractice,
                identityId: data?.identityId,
            });
            if (added.length === 0) {
                message.info('No new tags to add.');
                return;
            }
            message.success('Tags updated.');
            await refreshProspectTagList();
            onUpdate();
        },
        [
            currentTagNames,
            tagClientId,
            normalizeTags,
            onUpdate,
            refreshProspectTagList,
            isGroupPractice,
            data?.identityId,
        ],
    );

    const handleRemoveTag = React.useCallback(
        async (tagName: string) => {
            if (!tagClientId) return;
            const { removed } = await removeProspectTagByName({
                prospectId: tagClientId,
                tagList,
                tagName,
                isPracticeView: isGroupPractice,
            });
            if (!removed) return;
            message.success('Tags updated.');
            await refreshProspectTagList();
            onUpdate();
        },
        [tagClientId, onUpdate, refreshProspectTagList, tagList, isGroupPractice],
    );

    const handleReorderTags = React.useCallback(
        async (next: string[]) => {
            if (!tagClientId) return;
            if (tagList.length === 0) return;
            const { items } = await reorderProspectTagsByNames({
                prospectId: tagClientId,
                tagList,
                nextTagNames: next,
                isPracticeView: isGroupPractice,
                identityId: data?.identityId,
            });
            if (items.length === 0) return;
            message.success('Tags updated.');
            await refreshProspectTagList();
            onUpdate();
        },
        [tagClientId, onUpdate, refreshProspectTagList, tagList, isGroupPractice, data?.identityId],
    );

    //const intakeFormCompleted = true;
    if (intakeFormCompleted) {
        displayIntakeTab = showIntakeTab && allIntakeAnswers && allIntakeAnswers.length > 0;
    }

    //displayIntakeTab = true;

    React.useEffect(() => {
        if (textVisitItem || videoVisitItem) {
            // const formGroup = [
            //     {
            //         businessType: 'APPOINTMENT',
            //         businessId: 2188,
            //         formType: 'SPECIFIC_INTAKE_FORM',
            //         answers: [
            //             {
            //                 id: 58,
            //                 klarityUserFormId: 21,
            //                 sectionName: 'Dev-Psychiatry Initial Appointment Intake',
            //                 fullQuestionName: 'What treatment were you hoping to speak with a practitioner about?',
            //                 fullQuestionNameMd5: 'a82e9bc24baeb7a0b56cb50fbc9cc994',
            //                 shortQuestionName: 'What treatment were you hoping to speak with a practitioner about?',
            //                 answer: 'ADHD',
            //                 score: 0,
            //                 totalScore: null,
            //                 sort: 0,
            //                 createdAt: '2026-02-02T23:46:27.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:49.000+00:00',
            //             },
            //             {
            //                 id: 57,
            //                 klarityUserFormId: 21,
            //                 sectionName: '4. Mental Health Screening',
            //                 fullQuestionName:
            //                     '1. How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done? [Scoring]',
            //                 fullQuestionNameMd5: '3e59095dd1b8042d13391df425633bd7',
            //                 shortQuestionName:
            //                     '1. How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done? [Scoring]',
            //                 answer: '[1] - Rarely',
            //                 score: 1,
            //                 totalScore: null,
            //                 sort: 1,
            //                 createdAt: '2026-02-02T23:46:26.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:49.000+00:00',
            //             },
            //             {
            //                 id: 59,
            //                 klarityUserFormId: 21,
            //                 sectionName: 'Dev-Psychiatry Initial Appointment Intake',
            //                 fullQuestionName: 'What treatment were you hoping to speak with a practitioner about?',
            //                 fullQuestionNameMd5: 'a82e9bc24baeb7a0b56cb50fbc9cc994',
            //                 shortQuestionName: 'What treatment were you hoping to speak with a practitioner about?',
            //                 answer: 'Depression',
            //                 score: 0,
            //                 totalScore: null,
            //                 sort: 1,
            //                 createdAt: '2026-02-02T23:46:27.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:50.000+00:00',
            //             },
            //             {
            //                 id: 60,
            //                 klarityUserFormId: 21,
            //                 sectionName: 'Dev-Psychiatry Initial Appointment Intake',
            //                 fullQuestionName: 'What treatment were you hoping to speak with a practitioner about?',
            //                 fullQuestionNameMd5: 'a82e9bc24baeb7a0b56cb50fbc9cc994',
            //                 shortQuestionName: 'What treatment were you hoping to speak with a practitioner about?',
            //                 answer: 'Anxiety',
            //                 score: 0,
            //                 totalScore: null,
            //                 sort: 2,
            //                 createdAt: '2026-02-02T23:46:27.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:49.000+00:00',
            //             },
            //             {
            //                 id: 61,
            //                 klarityUserFormId: 21,
            //                 sectionName: 'Dev-Psychiatry Initial Appointment Intake',
            //                 fullQuestionName: 'What treatment were you hoping to speak with a practitioner about?',
            //                 fullQuestionNameMd5: 'a82e9bc24baeb7a0b56cb50fbc9cc994',
            //                 shortQuestionName: 'What treatment were you hoping to speak with a practitioner about?',
            //                 answer: 'Insomnia',
            //                 score: 0,
            //                 totalScore: null,
            //                 sort: 3,
            //                 createdAt: '2026-02-02T23:46:27.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:49.000+00:00',
            //             },
            //             {
            //                 id: 62,
            //                 klarityUserFormId: 21,
            //                 sectionName: '测试当选题',
            //                 fullQuestionName: 'Have you ever been hospitalized for mental health reasons?',
            //                 fullQuestionNameMd5: 'e28e59a2175e4b507fbc586923a3ee13',
            //                 shortQuestionName: 'Have you ever been hospitalized for mental health reasons?',
            //                 answer: 'Yes',
            //                 score: 0,
            //                 totalScore: null,
            //                 sort: 4,
            //                 createdAt: '2026-02-02T23:46:27.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:51.000+00:00',
            //             },
            //             {
            //                 id: 63,
            //                 klarityUserFormId: 21,
            //                 sectionName: '测试当选题',
            //                 fullQuestionName: 'Have you ever been hospitalized for mental health reasons?',
            //                 fullQuestionNameMd5: 'e28e59a2175e4b507fbc586923a3ee13',
            //                 shortQuestionName: 'Have you ever been hospitalized for mental health reasons?',
            //                 answer: 'No',
            //                 score: 0,
            //                 totalScore: null,
            //                 sort: 5,
            //                 createdAt: '2026-02-02T23:46:27.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:52.000+00:00',
            //             },
            //             {
            //                 id: 64,
            //                 klarityUserFormId: 21,
            //                 sectionName: '测试当选题',
            //                 fullQuestionName: 'Have you ever been hospitalized for mental health reasons?',
            //                 fullQuestionNameMd5: 'e28e59a2175e4b507fbc586923a3ee13',
            //                 shortQuestionName: 'Have you ever been hospitalized for mental health reasons?',
            //                 answer: 'Maybe',
            //                 score: 0,
            //                 totalScore: null,
            //                 sort: 6,
            //                 createdAt: '2026-02-02T23:46:27.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:49.000+00:00',
            //             },
            //             {
            //                 id: 65,
            //                 klarityUserFormId: 21,
            //                 sectionName: '长文本必填',
            //                 fullQuestionName:
            //                     'What is your main goal for treatment? (Examples: better focus, less anxiety, improved mood, better sleep, etc.) Your answer',
            //                 fullQuestionNameMd5: '2cd318ecce0bd0b0c2dcbcdab9143e06',
            //                 shortQuestionName:
            //                     'What is your main goal for treatment? (Examples: better focus, less anxiety, improved mood, better sleep, etc.) Your answer',
            //                 answer: '',
            //                 score: 0,
            //                 totalScore: null,
            //                 sort: 7,
            //                 createdAt: '2026-02-02T23:46:28.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:51.000+00:00',
            //             },
            //             {
            //                 id: 66,
            //                 klarityUserFormId: 21,
            //                 sectionName: '4. Mental Health Screening',
            //                 fullQuestionName:
            //                     '1. How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done? [Scoring]',
            //                 fullQuestionNameMd5: '3e59095dd1b8042d13391df425633bd7',
            //                 shortQuestionName:
            //                     '1. How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done? [Scoring]',
            //                 answer: '[0] - Never',
            //                 score: 0,
            //                 totalScore: null,
            //                 sort: 8,
            //                 createdAt: '2026-02-02T23:46:28.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:49.000+00:00',
            //             },
            //             {
            //                 id: 67,
            //                 klarityUserFormId: 21,
            //                 sectionName: '4. Mental Health Screening',
            //                 fullQuestionName:
            //                     '1. How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done? [Scoring]',
            //                 fullQuestionNameMd5: '3e59095dd1b8042d13391df425633bd7',
            //                 shortQuestionName:
            //                     '1. How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done? [Scoring]',
            //                 answer: '[2] - Sometimes',
            //                 score: 2,
            //                 totalScore: null,
            //                 sort: 10,
            //                 createdAt: '2026-02-02T23:46:28.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:51.000+00:00',
            //             },
            //             {
            //                 id: 68,
            //                 klarityUserFormId: 21,
            //                 sectionName: '4. Mental Health Screening',
            //                 fullQuestionName:
            //                     '1. How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done? [Scoring]',
            //                 fullQuestionNameMd5: '3e59095dd1b8042d13391df425633bd7',
            //                 shortQuestionName:
            //                     '1. How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done? [Scoring]',
            //                 answer: '[3] - Often',
            //                 score: 3,
            //                 totalScore: null,
            //                 sort: 11,
            //                 createdAt: '2026-02-02T23:46:28.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:51.000+00:00',
            //             },
            //             {
            //                 id: 69,
            //                 klarityUserFormId: 21,
            //                 sectionName: '4. Mental Health Screening',
            //                 fullQuestionName:
            //                     '1. How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done? [Scoring]',
            //                 fullQuestionNameMd5: '3e59095dd1b8042d13391df425633bd7',
            //                 shortQuestionName:
            //                     '1. How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done? [Scoring]',
            //                 answer: '[4] - Very Often',
            //                 score: 4,
            //                 totalScore: null,
            //                 sort: 12,
            //                 createdAt: '2026-02-02T23:46:28.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:50.000+00:00',
            //             },
            //             {
            //                 id: 70,
            //                 klarityUserFormId: 21,
            //                 sectionName: '4. Mental Health Screening',
            //                 fullQuestionName:
            //                     '2. How often do you have difficulty getting things in order when you have to do a task that requires organization?[Scoring]',
            //                 fullQuestionNameMd5: 'fa0ba4e3d63dfdc10d3f6b42c7db2e8c',
            //                 shortQuestionName:
            //                     '2. How often do you have difficulty getting things in order when you have to do a task that requires organization?[Scoring]',
            //                 answer: '[0] - Never',
            //                 score: 0,
            //                 totalScore: null,
            //                 sort: 13,
            //                 createdAt: '2026-02-02T23:46:28.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:50.000+00:00',
            //             },
            //             {
            //                 id: 71,
            //                 klarityUserFormId: 21,
            //                 sectionName: '4. Mental Health Screening',
            //                 fullQuestionName:
            //                     '2. How often do you have difficulty getting things in order when you have to do a task that requires organization?[Scoring]',
            //                 fullQuestionNameMd5: 'fa0ba4e3d63dfdc10d3f6b42c7db2e8c',
            //                 shortQuestionName:
            //                     '2. How often do you have difficulty getting things in order when you have to do a task that requires organization?[Scoring]',
            //                 answer: '[1] - Rarely',
            //                 score: 1,
            //                 totalScore: null,
            //                 sort: 14,
            //                 createdAt: '2026-02-02T23:46:28.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:50.000+00:00',
            //             },
            //             {
            //                 id: 72,
            //                 klarityUserFormId: 21,
            //                 sectionName: '4. Mental Health Screening',
            //                 fullQuestionName:
            //                     '2. How often do you have difficulty getting things in order when you have to do a task that requires organization?[Scoring]',
            //                 fullQuestionNameMd5: 'fa0ba4e3d63dfdc10d3f6b42c7db2e8c',
            //                 shortQuestionName:
            //                     '2. How often do you have difficulty getting things in order when you have to do a task that requires organization?[Scoring]',
            //                 answer: '[2] - Sometimes',
            //                 score: 2,
            //                 totalScore: null,
            //                 sort: 15,
            //                 createdAt: '2026-02-02T23:46:29.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:50.000+00:00',
            //             },
            //             {
            //                 id: 73,
            //                 klarityUserFormId: 21,
            //                 sectionName: '4. Mental Health Screening',
            //                 fullQuestionName:
            //                     '2. How often do you have difficulty getting things in order when you have to do a task that requires organization?[Scoring]',
            //                 fullQuestionNameMd5: 'fa0ba4e3d63dfdc10d3f6b42c7db2e8c',
            //                 shortQuestionName:
            //                     '2. How often do you have difficulty getting things in order when you have to do a task that requires organization?[Scoring]',
            //                 answer: '[3] - Often',
            //                 score: 3,
            //                 totalScore: null,
            //                 sort: 16,
            //                 createdAt: '2026-02-02T23:46:29.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:50.000+00:00',
            //             },
            //             {
            //                 id: 74,
            //                 klarityUserFormId: 21,
            //                 sectionName: '4. Mental Health Screening',
            //                 fullQuestionName:
            //                     '2. How often do you have difficulty getting things in order when you have to do a task that requires organization?[Scoring]',
            //                 fullQuestionNameMd5: 'fa0ba4e3d63dfdc10d3f6b42c7db2e8c',
            //                 shortQuestionName:
            //                     '2. How often do you have difficulty getting things in order when you have to do a task that requires organization?[Scoring]',
            //                 answer: '[4] - Very Often',
            //                 score: 4,
            //                 totalScore: null,
            //                 sort: 17,
            //                 createdAt: '2026-02-02T23:46:29.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:50.000+00:00',
            //             },
            //             {
            //                 id: 75,
            //                 klarityUserFormId: 21,
            //                 sectionName: 'B. Anxiety – GAD-7',
            //                 fullQuestionName: '1. Feeling nervous, anxious, or on edge [Scoring]',
            //                 fullQuestionNameMd5: '4b44195b0858c092d8940fcda4003756',
            //                 shortQuestionName: '1. Feeling nervous, anxious, or on edge [Scoring]',
            //                 answer: '[0] - Not at all',
            //                 score: 0,
            //                 totalScore: null,
            //                 sort: 18,
            //                 createdAt: '2026-02-02T23:46:29.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:49.000+00:00',
            //             },
            //             {
            //                 id: 76,
            //                 klarityUserFormId: 21,
            //                 sectionName: 'B. Anxiety – GAD-7',
            //                 fullQuestionName: '1. Feeling nervous, anxious, or on edge [Scoring]',
            //                 fullQuestionNameMd5: '4b44195b0858c092d8940fcda4003756',
            //                 shortQuestionName: '1. Feeling nervous, anxious, or on edge [Scoring]',
            //                 answer: '[1] - Several Days',
            //                 score: 1,
            //                 totalScore: null,
            //                 sort: 19,
            //                 createdAt: '2026-02-02T23:46:29.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:51.000+00:00',
            //             },
            //             {
            //                 id: 77,
            //                 klarityUserFormId: 21,
            //                 sectionName: 'B. Anxiety – GAD-7',
            //                 fullQuestionName: '1. Feeling nervous, anxious, or on edge [Scoring]',
            //                 fullQuestionNameMd5: '4b44195b0858c092d8940fcda4003756',
            //                 shortQuestionName: '1. Feeling nervous, anxious, or on edge [Scoring]',
            //                 answer: '[2] - More than half the days',
            //                 score: 2,
            //                 totalScore: null,
            //                 sort: 20,
            //                 createdAt: '2026-02-02T23:46:29.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:50.000+00:00',
            //             },
            //             {
            //                 id: 78,
            //                 klarityUserFormId: 21,
            //                 sectionName: 'B. Anxiety – GAD-7',
            //                 fullQuestionName: '1. Feeling nervous, anxious, or on edge [Scoring]',
            //                 fullQuestionNameMd5: '4b44195b0858c092d8940fcda4003756',
            //                 shortQuestionName: '1. Feeling nervous, anxious, or on edge [Scoring]',
            //                 answer: '[3] - Nearly every day',
            //                 score: 3,
            //                 totalScore: null,
            //                 sort: 21,
            //                 createdAt: '2026-02-02T23:46:30.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:51.000+00:00',
            //             },
            //             {
            //                 id: 79,
            //                 klarityUserFormId: 21,
            //                 sectionName: 'B. Anxiety – GAD-7',
            //                 fullQuestionName: '2. Not being able to stop or control worrying',
            //                 fullQuestionNameMd5: '04f22427bb82faf97d9b087484587a77',
            //                 shortQuestionName: '2. Not being able to stop or control worrying',
            //                 answer: '[0] - Not at all',
            //                 score: 0,
            //                 totalScore: null,
            //                 sort: 22,
            //                 createdAt: '2026-02-02T23:46:30.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:50.000+00:00',
            //             },
            //             {
            //                 id: 80,
            //                 klarityUserFormId: 21,
            //                 sectionName: 'B. Anxiety – GAD-7',
            //                 fullQuestionName: '2. Not being able to stop or control worrying',
            //                 fullQuestionNameMd5: '04f22427bb82faf97d9b087484587a77',
            //                 shortQuestionName: '2. Not being able to stop or control worrying',
            //                 answer: '[1] - Several Days',
            //                 score: 1,
            //                 totalScore: null,
            //                 sort: 23,
            //                 createdAt: '2026-02-02T23:46:30.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:51.000+00:00',
            //             },
            //             {
            //                 id: 81,
            //                 klarityUserFormId: 21,
            //                 sectionName: 'B. Anxiety – GAD-7',
            //                 fullQuestionName: '2. Not being able to stop or control worrying',
            //                 fullQuestionNameMd5: '04f22427bb82faf97d9b087484587a77',
            //                 shortQuestionName: '2. Not being able to stop or control worrying',
            //                 answer: '[2] - More than half the days',
            //                 score: 2,
            //                 totalScore: null,
            //                 sort: 24,
            //                 createdAt: '2026-02-02T23:46:30.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:51.000+00:00',
            //             },
            //             {
            //                 id: 82,
            //                 klarityUserFormId: 21,
            //                 sectionName: 'B. Anxiety – GAD-7',
            //                 fullQuestionName: '2. Not being able to stop or control worrying',
            //                 fullQuestionNameMd5: '04f22427bb82faf97d9b087484587a77',
            //                 shortQuestionName: '2. Not being able to stop or control worrying',
            //                 answer: '[3] - Nearly every day',
            //                 score: 3,
            //                 totalScore: null,
            //                 sort: 25,
            //                 createdAt: '2026-02-02T23:46:30.000+00:00',
            //                 updatedAt: '2026-02-02T15:47:51.000+00:00',
            //             },
            //         ],
            //     },
            //     {
            //         businessType: 'APPOINTMENT',
            //         businessId: 2188,
            //         formType: 'BASIC_INITIAL_INTAKE_FORM',
            //         answers: [],
            //     },
            // ];
            const formGroup = textVisitItem?.formGroups || videoVisitItem?.formGroups || [];
            setAllIntakeAnswers(collectSpecificIntakeAnswers(formGroup));
        }
    }, [textVisitItem, videoVisitItem]);

    const intakeFormParseResult = React.useMemo(
        () => parseSpecificIntakeFormGroups(textVisitItem?.formGroups || videoVisitItem?.formGroups),
        [textVisitItem, videoVisitItem],
    );
    const intakeAssessmentNavItems = intakeFormParseResult?.navItems ?? [];
    const groupedIntakeAnswers = intakeFormParseResult?.groupedIntakeAnswers ?? {};
    const commonIntakeAnswers = intakeFormParseResult?.commonIntakeAnswers ?? [];

    const handleOpenPatientUpdateDialog = React.useCallback((type: PatientUpdateType) => {
        return () => {
            setPatientUpdateDialog({
                type,
                open: true,
            });
        };
    }, []);

    const getIntakeFormUrl = () => {
        if (textVisitItem?.id) return getIntakeFormUrlByTextVisitId(textVisitItem.id);
        if (videoVisitItem?.id) return getIntakeFormUrlByAppointmentId(videoVisitItem.id);
        return undefined;
    };
    const intakeFormUrl = getIntakeFormUrl();

    const handleSendIntakeFormReminder = async () => {
        if (!intakeFormUrl) return;
        const clientName = `${data?.firstName || ''} ${data?.lastName || ''}`;

        const reminderMessage = `Hi ${clientName || 'Patient'},
Just a quick reminder to complete your intake form to help us prepare for your visit. Please fill it out at your earliest convenience using the link below:
<a href="${intakeFormUrl}" target="_blank">${intakeFormUrl}</a>
If you have any questions, feel free to reach out. We're here to help!`;

        setSendingIntakeReminder(true);
        const res = await sendMessage({
            providerId: getUserId()!,
            clientId: data?.id!,
            message: reminderMessage.replace(/<[^>]+>/g, ''),
        });
        setSendingIntakeReminder(false);

        if (!res.error) {
            message.success('Intake form reminder sent successfully!');

            //end add local message
        }
    };

    // 格式化电话号码为 (XXX)XXX-XXXX 格式
    const formatPhoneNumber = (phone: string | null | undefined): string => {
        if (!phone) return '';
        // 移除非数字字符
        const digits = phone.replace(/\D/g, '');
        // 如果是10位数字，格式化为 (XXX)XXX-XXXX
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        // 如果是11位数字且以1开头（美国国家代码），格式化为 (XXX)XXX-XXXX
        if (digits.length === 11 && digits[0] === '1') {
            return `(${digits.slice(1, 4)})${digits.slice(4, 7)}-${digits.slice(7)}`;
        }
        // 其他情况返回原始值
        return phone;
    };

    const closeChargeClinetPopover = () => {
        setShowChargeClientPopover(false);
        localStorage.setItem(DISABLE_CHARGE_PATIENTS_POPUP, 'true');
    };

    // 获取 ID card 图片预览 URL
    React.useEffect(() => {
        const fetchIdCardUrls = async () => {
            const _isOps = isOpPortal();
            const fetchUrl = async (resourceKey: string | null | undefined) => {
                if (resourceKey?.includes('http')) {
                    return resourceKey;
                }
                if (!resourceKey) return undefined;
                try {
                    const res = _isOps
                        ? await getPrivateFilePreview(resourceKey)
                        : await getProviderPrivateFileSigned(resourceKey);
                    if (!res.error && res.data?.data?.singedUrl) {
                        return res.data.data.singedUrl;
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error(`Failed to fetch image URL for resourceKey: ${resourceKey}`, error);
                }
                return undefined;
            };

            const frontUrl = await fetchUrl(data?.idFront);
            const backUrl = await fetchUrl(data?.idBack);
            setIdFrontUrl(frontUrl);
            setIdBackUrl(backUrl);
        };

        if (data?.idFront || data?.idBack) {
            fetchIdCardUrls();
        } else {
            setIdFrontUrl(undefined);
            setIdBackUrl(undefined);
        }
    }, [data?.idFront, data?.idBack]);

    const items = [
        {
            label: 'Basic info',
            key: '1',
            children: (
                <Tabs
                    activeKey={subTabKey}
                    onChange={(key) => {
                        setSubTabKey(key);
                        setEditType(parseInt(key, 10) as 1 | 2);
                    }}
                    className={s.subTabs}
                    tabBarStyle={{ border: 'none', margin: 0 }}
                    items={[
                        {
                            label: 'Personal info',
                            key: '1',
                            children: (
                                <div className={s.infoWrap}>
                                    <div>
                                        {!isSample && !isSimpleType && !isOps && (
                                            <Button
                                                className={s.editBtn}
                                                onClick={() => {
                                                    setEditType(1);
                                                    setShowEdit(true);
                                                }}
                                            >
                                                <div className={s.btnWrap}>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M13.5118 2.48816L14.0421 1.95783L14.0421 1.95783L13.5118 2.48816ZM4.33333 14.0237V14.7737C4.53225 14.7737 4.72301 14.6946 4.86366 14.554L4.33333 14.0237ZM2 14.0237H1.25C1.25 14.4379 1.58579 14.7737 2 14.7737V14.0237ZM2 11.6429L1.46967 11.1126C1.32902 11.2533 1.25 11.444 1.25 11.6429H2ZM11.1548 2.48816L11.6851 3.01849C12.0431 2.6605 12.6235 2.6605 12.9815 3.01849L13.5118 2.48816L14.0421 1.95783C13.0984 1.01406 11.5682 1.01406 10.6245 1.95783L11.1548 2.48816ZM13.5118 2.48816L12.9815 3.01849C13.3395 3.37647 13.3395 3.95687 12.9815 4.31485L13.5118 4.84518L14.0421 5.37551C14.9859 4.43174 14.9859 2.90159 14.0421 1.95783L13.5118 2.48816ZM13.5118 4.84518L12.9815 4.31485L3.803 13.4933L4.33333 14.0237L4.86366 14.554L14.0421 5.37551L13.5118 4.84518ZM4.33333 14.0237V13.2737H2V14.0237V14.7737H4.33333V14.0237ZM11.1548 2.48816L10.6245 1.95783L1.46967 11.1126L2 11.6429L2.53033 12.1733L11.6851 3.01849L11.1548 2.48816ZM2 11.6429H1.25V14.0237H2H2.75V11.6429H2ZM10.1548 3.48816L9.62445 4.01849L11.9815 6.37551L12.5118 5.84518L13.0421 5.31485L10.6851 2.95783L10.1548 3.48816Z"
                                                            fill="#6B7280"
                                                        />
                                                    </svg>
                                                    Edit
                                                </div>
                                            </Button>
                                        )}
                                    </div>
                                    {/* Basic Information */}
                                    <div className={s.row}>
                                        <div className={s.displayItem} style={{ width: '300px' }}>
                                            <div className={s.label}>Phone number</div>
                                            <div className={s.value}>
                                                {displayItem(
                                                    loading,
                                                    EMPTY_PLACEHOLDER_WITH_DASH,
                                                    data?.tel ? formatPhoneNumber(data.tel) : '',
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div className={s.displayItem} style={{ width: '300px' }}>
                                            <div className={s.label}>Email</div>
                                            <div className={s.value}>{data?.email}</div>
                                        </div>
                                    </div>
                                    {/* Personal Information */}
                                    <div className={s.row}>
                                        <div className={s.displayItem} style={{ width: '300px' }}>
                                            <div className={s.label}>Date of birth</div>
                                            <div className={s.value}>
                                                {data?.birthday
                                                    ? getTimeStr(dayjs(data?.birthday), 'MMMM, DD, YYYY')
                                                    : displayItem(loading, EMPTY_PLACEHOLDER_WITH_DASH, '')}
                                            </div>
                                        </div>
                                        <div className={s.displayItem} style={{ width: '150px' }}>
                                            <div className={s.label}>Age</div>
                                            <div className={s.value}>
                                                {displayItem(
                                                    loading,
                                                    EMPTY_PLACEHOLDER_WITH_DASH,
                                                    getAgeFromBirthday(data?.birthday),
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div className={s.displayItem} style={{ width: '300px' }}>
                                            <div className={s.label}>Gender</div>
                                            <div className={s.value}>
                                                {data?.gender
                                                    ? data?.gender
                                                    : displayItem(loading, EMPTY_PLACEHOLDER_WITH_DASH, '')}
                                            </div>
                                        </div>
                                        <div className={s.displayItem} style={{ width: '150px' }}>
                                            <div className={s.label}>Marital status</div>
                                            <div className={s.value}>
                                                {(() => {
                                                    if (!data?.maritalStatus) {
                                                        return displayItem(loading, EMPTY_PLACEHOLDER_WITH_DASH, '');
                                                    }
                                                    if (
                                                        Object.values(MaritalStatusValue).includes(
                                                            data.maritalStatus as MaritalStatusValue,
                                                        )
                                                    ) {
                                                        // Convert to lowercase, replace underscores with spaces, then capitalize only first letter
                                                        const text = data.maritalStatus
                                                            .toLowerCase()
                                                            .replace(/_/g, ' ');
                                                        return text.charAt(0).toUpperCase() + text.slice(1);
                                                    }
                                                    return data.maritalStatus;
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                    {/* Contact Information */}
                                    <div className={s.row}>
                                        <div className={s.displayItem}>
                                            <div className={s.label}>Address</div>
                                            <div className={s.value}>
                                                {displayItem(
                                                    loading,
                                                    EMPTY_PLACEHOLDER_WITH_DASH,
                                                    `${data?.address ? data?.address : ''}${
                                                        data?.city ? `, ${data?.city}` : ''
                                                    }${data?.state ? `, ${data?.state}` : ''}${
                                                        data?.zip ? `, ${data?.zip}` : ''
                                                    }`,
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div className={s.displayItem}>
                                            <div className={s.label}>Preferred contact method</div>
                                            <div className={s.value}>
                                                {data?.preferMethod
                                                    ? data?.preferMethod
                                                          ?.split(',')
                                                          .map((m) => m.trim())
                                                          .join(', ')
                                                    : displayItem(loading, EMPTY_PLACEHOLDER_WITH_DASH, '')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div className={s.displayItem}>
                                            <div className={s.label}>Emergency contact</div>
                                            <div
                                                className={s.value}
                                                style={{
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                }}
                                            >
                                                {data?.emergencyContacts && data.emergencyContacts.length > 0 ? (
                                                    <>
                                                        <div>
                                                            {data.emergencyContacts[0].fullName}
                                                            {data.emergencyContacts[0].relationship
                                                                ? ` (${data.emergencyContacts[0].relationship})`
                                                                : ''}
                                                        </div>
                                                        <div>{formatPhoneNumber(data.emergencyContacts[0].phone)}</div>
                                                    </>
                                                ) : (
                                                    displayItem(loading, EMPTY_PLACEHOLDER_WITH_DASH, '')
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {(data?.idFront || data?.idBack) && (
                                        <div className={s.row}>
                                            <div className={s.displayItem}>
                                                <div className={s.label}>Front of ID card</div>
                                                {(() => {
                                                    if (idFrontUrl) {
                                                        return (
                                                            <div className={s.value}>
                                                                <img src={idFrontUrl} className={s.insurImg} />
                                                                <span
                                                                    onClick={() => {
                                                                        setViewImgUrl(idFrontUrl);
                                                                    }}
                                                                    className={s.viewImg}
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="14"
                                                                        height="14"
                                                                        viewBox="0 0 14 14"
                                                                        fill="none"
                                                                    >
                                                                        <path
                                                                            d="M2.33398 4.66634V2.33301M2.33398 2.33301H4.66732M2.33398 2.33301L5.25065 5.24967M11.6673 4.66634V2.33301M11.6673 2.33301H9.33398M11.6673 2.33301L8.75065 5.24967M2.33398 9.33301V11.6663M2.33398 11.6663H4.66732M2.33398 11.6663L5.25065 8.74967M11.6673 11.6663L8.75065 8.74967M11.6673 11.6663V9.33301M11.6673 11.6663H9.33398"
                                                                            stroke="#00816B"
                                                                            strokeWidth="1.16667"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </span>
                                                            </div>
                                                        );
                                                    }
                                                    if (data?.idFront) {
                                                        return <div className={s.value}>Loading...</div>;
                                                    }
                                                    return <div className={s.value}>-</div>;
                                                })()}
                                            </div>
                                            <div className={s.displayItem}>
                                                <div className={s.label}>Back of ID card</div>
                                                {(() => {
                                                    if (idBackUrl) {
                                                        return (
                                                            <div className={s.value}>
                                                                <img src={idBackUrl} className={s.insurImg} />
                                                                <span
                                                                    onClick={() => {
                                                                        setViewImgUrl(idBackUrl);
                                                                    }}
                                                                    className={s.viewImg}
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="14"
                                                                        height="14"
                                                                        viewBox="0 0 14 14"
                                                                        fill="none"
                                                                    >
                                                                        <path
                                                                            d="M2.33398 4.66634V2.33301M2.33398 2.33301H4.66732M2.33398 2.33301L5.25065 5.24967M11.6673 4.66634V2.33301M11.6673 2.33301H9.33398M11.6673 2.33301L8.75065 5.24967M2.33398 9.33301V11.6663M2.33398 11.6663H4.66732M2.33398 11.6663L5.25065 8.74967M11.6673 11.6663L8.75065 8.74967M11.6673 11.6663V9.33301M11.6673 11.6663H9.33398"
                                                                            stroke="#00816B"
                                                                            strokeWidth="1.16667"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </span>
                                                            </div>
                                                        );
                                                    }
                                                    if (data?.idBack) {
                                                        return <div className={s.value}>Loading...</div>;
                                                    }
                                                    return <div className={s.value}>-</div>;
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ),
                        },
                        {
                            label: 'Insurance & payment',
                            key: '2',
                            children: (
                                <div className={s.infoWrap}>
                                    {showBillingForVisit && (
                                        <BillingForVisit
                                            videoVisitItem={videoVisitItem}
                                            textVisitItem={textVisitItem}
                                        />
                                    )}
                                    <div className={s.insActions}>
                                        {!isSample && !isSimpleType && !isOps && (
                                            <Button
                                                className={s.editBtn}
                                                onClick={() => {
                                                    setEditType(2);
                                                    setShowEdit(true);
                                                }}
                                            >
                                                <div className={s.btnWrap}>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M13.5118 2.48816L14.0421 1.95783L14.0421 1.95783L13.5118 2.48816ZM4.33333 14.0237V14.7737C4.53225 14.7737 4.72301 14.6946 4.86366 14.554L4.33333 14.0237ZM2 14.0237H1.25C1.25 14.4379 1.58579 14.7737 2 14.7737V14.0237ZM2 11.6429L1.46967 11.1126C1.32902 11.2533 1.25 11.444 1.25 11.6429H2ZM11.1548 2.48816L11.6851 3.01849C12.0431 2.6605 12.6235 2.6605 12.9815 3.01849L13.5118 2.48816L14.0421 1.95783C13.0984 1.01406 11.5682 1.01406 10.6245 1.95783L11.1548 2.48816ZM13.5118 2.48816L12.9815 3.01849C13.3395 3.37647 13.3395 3.95687 12.9815 4.31485L13.5118 4.84518L14.0421 5.37551C14.9859 4.43174 14.9859 2.90159 14.0421 1.95783L13.5118 2.48816ZM13.5118 4.84518L12.9815 4.31485L3.803 13.4933L4.33333 14.0237L4.86366 14.554L14.0421 5.37551L13.5118 4.84518ZM4.33333 14.0237V13.2737H2V14.0237V14.7737H4.33333V14.0237ZM11.1548 2.48816L10.6245 1.95783L1.46967 11.1126L2 11.6429L2.53033 12.1733L11.6851 3.01849L11.1548 2.48816ZM2 11.6429H1.25V14.0237H2H2.75V11.6429H2ZM10.1548 3.48816L9.62445 4.01849L11.9815 6.37551L12.5118 5.84518L13.0421 5.31485L10.6851 2.95783L10.1548 3.48816Z"
                                                            fill="#6B7280"
                                                        />
                                                    </svg>
                                                    Edit
                                                </div>
                                            </Button>
                                        )}
                                        {/* 常驻工具（原型 #insActions）：不依赖失败状态也能主动向患者要新卡（无 AAA 上下文）。
                                            practice 视图没选 provider 时置灰：请求以那位 provider 的身份发出、
                                            落在他名下的记录上，没有选中就没有收发双方 */}
                                        {eligApi.enabled && !isSample && !isSimpleType && !isOps && (
                                            <Tooltip title={needsProviderPick ? CHOOSE_PROVIDER_CARD_TIP : ''}>
                                                {/* disabled 的按钮不发鼠标事件，tooltip 要挂在外层 span 上 */}
                                                <span className={s.cardReqBtnWrap}>
                                                    <Button
                                                        className={s.editBtn}
                                                        disabled={needsProviderPick}
                                                        onClick={() => {
                                                            eligApi.openCardRequest();
                                                        }}
                                                    >
                                                        Request updated insurance
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                    {/* 10/21：eligibility 模块在 Edit 下、Insurance 标题之上——整个患者一个答案，
                                        先读答案再读各张卡的字段；这个 tab 被打开就是为了回答「这次 visit 能不能计费」 */}
                                    {eligApi.enabled && <EligibilityModule api={eligApi} />}
                                    {data?.insuranceSwitch === false && (
                                        <div className={s.selfPayBanner}>
                                            <div className={s.selfPayTitle}>
                                                Patient prefers self-pay
                                                {data?.insuranceSwitchTime && (
                                                    <span className={s.selfPaySince}>
                                                        {` · since ${dayjs(data.insuranceSwitchTime).format('MMM D')}`}
                                                    </span>
                                                )}
                                            </div>
                                            <div className={s.selfPayDesc}>
                                                Set by the patient in their portal. Records below are unchanged.
                                            </div>
                                        </div>
                                    )}
                                    <div className={`${s.itemTitle} ${s.eligTitleRow}`}>
                                        {secondInsurance ? 'Primary insurance' : 'Insurance'}
                                        {/* 10/21：标题旁不再挂状态 tag——一组里有多张卡时，标题旁的 tag 说不清
                                            是哪张；状态由上方结果卡和 History 承担，Clients 列表仍一格 */}
                                        {/* History（原型 #eligHistoryBtn）：钉在标题行最右，因为它讲的是
                                            整条记录而不是某一张结果卡。有保险就常驻——按钮随条数出没像 bug，
                                            没得可列由弹窗自己说。例外是 practice 视图还没挑 provider：
                                            流水是按 (provider, patient) 取的，没有身份就没有可查的东西 */}
                                        {eligApi.enabled && eligApi.hasInsurance && !needsProviderPick && (
                                            /* 与模块内横幅的 Add it / Set it up 同款 small 按钮：
                                               标题行的次要动作，不该比它引出的横幅按钮还重 */
                                            <Button
                                                size="small"
                                                className={s.eligHistoryBtn}
                                                onClick={eligApi.openHistory}
                                            >
                                                {HISTORY_COPY.button}
                                            </Button>
                                        )}
                                    </div>
                                    <div className={s.row}>
                                        <div className={s.displayItem}>
                                            <div className={s.label}>Primary policy holder</div>
                                            <div className={s.value}>
                                                {insurance1?.policyHolderType === 'SOMEONE_ELSE' && insurance1?.relationship
                                                    ? `Client's ${insurance1.relationship.toLowerCase()}`
                                                    : 'Client'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div className={s.displayItem}>
                                            <div className={s.label}>Member legal first name</div>
                                            <div className={s.value}>
                                                {(insurance1?.policyHolderType === 'SOMEONE_ELSE'
                                                    ? insurance1?.holderFirstName
                                                    : insurance1?.firstName) || '-'}
                                            </div>
                                        </div>
                                        <div className={s.displayItem}>
                                            <div className={s.label}>Member legal last name</div>
                                            <div className={s.value}>
                                                {(insurance1?.policyHolderType === 'SOMEONE_ELSE'
                                                    ? insurance1?.holderLastName
                                                    : insurance1?.lastName) || '-'}
                                            </div>
                                        </div>
                                    </div>
                                    {insurance1?.policyHolderType === 'SOMEONE_ELSE' && (
                                        <div className={s.row}>
                                            <div className={s.displayItem}>
                                                <div className={s.label}>Member date of birth</div>
                                                <div className={s.value}>
                                                    {insurance1?.holderDob
                                                        ? dayjs(insurance1.holderDob, 'YYYY-MM-DD').format('MM/DD/YYYY')
                                                        : '-'}
                                                </div>
                                            </div>
                                            <div className={s.displayItem}>
                                                <div className={s.label}>Member sex</div>
                                                <div className={s.value}>{insurance1?.holderGender || '-'}</div>
                                            </div>
                                        </div>
                                    )}
                                    <div className={s.row}>
                                        <div className={s.displayItem}>
                                            <div className={s.label}>Insurance carrier</div>
                                            <div className={s.value}>{insurance1?.carrier || '-'}</div>
                                        </div>
                                        <div className={s.displayItem}>
                                            <div className={s.label}>Insurance plan</div>
                                            <div className={s.value}>{insurance1?.plan || '-'}</div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div className={s.displayItem} style={{ width: '150px' }}>
                                            <div className={s.label}>Member ID</div>
                                            <div className={s.value}>{insurance1?.memberId || '-'}</div>
                                        </div>
                                        <div className={s.displayItem} style={{ width: '150px' }}>
                                            <div className={s.label}>Group ID</div>
                                            <div className={s.value}>{insurance1?.groupId || '-'}</div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div className={s.displayItem}>
                                            <div className={s.label}>Front of insurance card</div>
                                            {insurance1?.front && (
                                                <div className={s.value}>
                                                    <img src={insurance1?.front} className={s.insurImg} />
                                                    <span
                                                        onClick={() => {
                                                            setViewImgUrl(insurance1?.front || undefined);
                                                        }}
                                                        className={s.viewImg}
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="14"
                                                            height="14"
                                                            viewBox="0 0 14 14"
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M2.33398 4.66634V2.33301M2.33398 2.33301H4.66732M2.33398 2.33301L5.25065 5.24967M11.6673 4.66634V2.33301M11.6673 2.33301H9.33398M11.6673 2.33301L8.75065 5.24967M2.33398 9.33301V11.6663M2.33398 11.6663H4.66732M2.33398 11.6663L5.25065 8.74967M11.6673 11.6663L8.75065 8.74967M11.6673 11.6663V9.33301M11.6673 11.6663H9.33398"
                                                                stroke="#00816B"
                                                                strokeWidth="1.16667"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                </div>
                                            )}
                                            {!insurance1?.front && <div className={s.value}>-</div>}
                                        </div>
                                        <div className={s.displayItem}>
                                            <div className={s.label}>Back of insurance card</div>
                                            {insurance1?.backend && (
                                                <div className={s.value}>
                                                    <img src={insurance1?.backend} className={s.insurImg} />
                                                    <span
                                                        onClick={() => {
                                                            setViewImgUrl(insurance1?.backend || undefined);
                                                        }}
                                                        className={s.viewImg}
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="14"
                                                            height="14"
                                                            viewBox="0 0 14 14"
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M2.33398 4.66634V2.33301M2.33398 2.33301H4.66732M2.33398 2.33301L5.25065 5.24967M11.6673 4.66634V2.33301M11.6673 2.33301H9.33398M11.6673 2.33301L8.75065 5.24967M2.33398 9.33301V11.6663M2.33398 11.6663H4.66732M2.33398 11.6663L5.25065 8.74967M11.6673 11.6663L8.75065 8.74967M11.6673 11.6663V9.33301M11.6673 11.6663H9.33398"
                                                                stroke="#00816B"
                                                                strokeWidth="1.16667"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                </div>
                                            )}
                                            {!insurance1?.backend && <div className={s.value}>-</div>}
                                        </div>
                                    </div>
                                    {(insurance1?.front || insurance1?.backend) && (
                                        <div className={s.row}>
                                            <div className={s.displayItem}>
                                                <div className={s.downloadInsuranceCard}>
                                                    <div
                                                        className={s.downloadInsuranceCardBtn}
                                                        onClick={() => {
                                                            handleDownloadPhoto(
                                                                [insurance1?.front || '', insurance1?.backend || ''],
                                                                'insurance_card',
                                                            );
                                                        }}
                                                    >
                                                        Download insurance card
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {morePrimaryCount > 0 && (
                                        <div className={s.row}>
                                            <span
                                                className={s.viewMoreLink}
                                                onClick={() => setShowInsurancePopup('PRIMARY')}
                                            >
                                                {`View ${morePrimaryCount} more primary ${morePrimaryCount === 1 ? 'insurance' : 'insurances'} →`}
                                            </span>
                                        </div>
                                    )}
                                    {secondInsurance && (
                                        <>
                                            <div className={s.itemTitle}>Secondary insurance</div>
                                            <div className={s.row}>
                                                <div className={s.displayItem}>
                                                    <div className={s.label}>Primary policy holder</div>
                                                    <div className={s.value}>
                                                        {insurance2?.policyHolderType === 'SOMEONE_ELSE' && insurance2?.relationship
                                                            ? `Client's ${insurance2.relationship.toLowerCase()}`
                                                            : 'Client'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={s.row}>
                                                <div className={s.displayItem}>
                                                    <div className={s.label}>Insurance carrier</div>
                                                    <div className={s.value}>{insurance2?.carrier || '-'}</div>
                                                </div>
                                                <div className={s.displayItem}>
                                                    <div className={s.label}>Insurance plan</div>
                                                    <div className={s.value}>{insurance2?.plan || '-'}</div>
                                                </div>
                                            </div>
                                            <div className={s.row}>
                                                <div className={s.displayItem} style={{ width: '150px' }}>
                                                    <div className={s.label}>Member ID</div>
                                                    <div className={s.value}>{insurance2?.memberId || '-'}</div>
                                                </div>
                                                <div className={s.displayItem} style={{ width: '150px' }}>
                                                    <div className={s.label}>Group ID</div>
                                                    <div className={s.value}>{insurance2?.groupId || '-'}</div>
                                                </div>
                                            </div>
                                            {moreSecondaryCount > 0 && (
                                                <div className={s.row}>
                                                    <span
                                                        className={s.viewMoreLink}
                                                        onClick={() => setShowInsurancePopup('SECONDARY')}
                                                    >
                                                        {`View ${moreSecondaryCount} more secondary insurance →`}
                                                    </span>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    <div className={s.itemTitle}>Payment</div>
                                    <div className={s.row}>
                                        <div className={s.displayItem} style={{ width: '200px' }}>
                                            <div className={s.label}>Card number</div>
                                            <div className={s.value}>{cNumber || '-'}</div>
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div className={s.displayItem} style={{ width: '200px' }}>
                                            <div className={s.label}>Expiration date</div>
                                            <div className={s.value}>{cExp || '-'}</div>
                                        </div>
                                    </div>
                                </div>
                            ),
                        },
                    ]}
                />
            ),
        },
        {
            label: 'Health Profile',
            key: '2',
            children: (
                <div className={s.healthProfile}>
                    <div className={s.healthSection}>
                        <div className={s.sectionTitle}>
                            <div className={s.sectionTitleText}>
                                Medical history
                                <UpdatedRecordPopover
                                    type="medicalHistory"
                                    prospectChangeContext={data?.prospectChangeContext}
                                    prospectData={data}
                                    onRecordRead={() => {
                                        onUpdate();
                                    }}
                                />
                            </div>
                            {!isOps && (
                                <div
                                    className={s.sectionTitleButton}
                                    onClick={handleOpenPatientUpdateDialog(PatientUpdateType.MedicalHistory)}
                                >
                                    <img src={PencilIcon} /> Edit
                                </div>
                            )}
                        </div>
                        <div className={s.healthTagsContainer}>
                            {data?.medicalHistory?.length ? (
                                data.medicalHistory.map((item) => <span className={s.healthTag}>{item}</span>)
                            ) : (
                                <div className="text-gray-900">No medical history provided</div>
                            )}
                        </div>
                    </div>

                    <div className={s.healthSection}>
                        <div className={s.sectionTitle}>
                            <div className={s.sectionTitleText}>
                                Medications
                                <UpdatedRecordPopover
                                    type="medications"
                                    prospectChangeContext={data?.prospectChangeContext}
                                    prospectData={data}
                                    onRecordRead={() => {
                                        onUpdate();
                                    }}
                                />
                            </div>
                            {!isOps && (
                                <div
                                    className={s.sectionTitleButton}
                                    onClick={handleOpenPatientUpdateDialog(PatientUpdateType.CurrentMedications)}
                                >
                                    <img src={PencilIcon} /> Edit
                                </div>
                            )}
                        </div>
                        <div className={s.healthTagsContainer}>
                            {data?.medications?.length ? (
                                data.medications.map((item) => <span className={s.healthTag}>{item}</span>)
                            ) : (
                                <div className="text-gray-900">No medications listed</div>
                            )}
                        </div>
                    </div>

                    <div className={s.healthSection}>
                        <div className={s.sectionTitle}>
                            <div className={s.sectionTitleText}>
                                Allergies
                                <UpdatedRecordPopover
                                    type="allergy"
                                    prospectChangeContext={data?.prospectChangeContext}
                                    prospectData={data}
                                    onRecordRead={() => {
                                        onUpdate();
                                    }}
                                />
                            </div>
                            {!isOps && (
                                <div
                                    className={s.sectionTitleButton}
                                    onClick={handleOpenPatientUpdateDialog(PatientUpdateType.Allergies)}
                                >
                                    <img src={PencilIcon} /> Edit
                                </div>
                            )}
                        </div>
                        <div className={s.healthTagsContainer}>
                            {data?.allergies?.length ? (
                                data.allergies.map((item) => <span className={s.healthTag}>{item}</span>)
                            ) : (
                                <div className="text-gray-900">No allergies reported</div>
                            )}
                        </div>
                    </div>
                </div>
            ),
        },
    ];
    if (displayIntakeTab) {
        items.push({
            label: 'Intake',
            key: '3',
            children: (
                <div className={s.iWrapper}>
                    {!intakeFormCompleted && (
                        <div className={s.intakeFormNotCompleted}>
                            <div className={s.intakeFormCompletedTitle}>Patient hasn’t completed their intake yet</div>
                            {intakeFormUrl && (
                                <Button
                                    className={s.intakeFormReminderBtn}
                                    loading={sendingIntakeReminder}
                                    onClick={handleSendIntakeFormReminder}
                                    type="text"
                                >
                                    <span>Send intake form reminder</span>
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        overlayInnerStyle={{
                                            width: '330px',
                                            padding: '16px',
                                            borderRadius: '24px',
                                        }}
                                        color="#ffffff"
                                        title={
                                            <div className={s.tooltipContent} style={{ color: '#333' }}>
                                                <div className={s.tooltipTitle}>
                                                    📩 Intake form reminder message preview
                                                </div>
                                                <div className={s.tooltipMessage}>
                                                    <p>Hi [Patient&apos;s Name],</p>
                                                    <p>
                                                        Just a quick reminder to complete your intake form to help us
                                                        prepare for your visit. Please fill it out at your earliest
                                                        convenience using the link below:
                                                    </p>
                                                    <p className={s.tooltipLink}>[Intake Form URL]</p>
                                                    <p>
                                                        If you have any questions, feel free to reach out. We&apos;re
                                                        here to help!
                                                    </p>
                                                </div>
                                            </div>
                                        }
                                        placement="top"
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 14 14"
                                            fill="none"
                                            className={s.questionIcon}
                                        >
                                            <path
                                                d="M4.48511 5C4.85118 4.22321 5.83895 3.66667 7.00004 3.66667C8.4728 3.66667 9.6667 4.5621 9.6667 5.66667C9.6667 6.59963 8.81496 7.38338 7.66285 7.6044C7.30125 7.67377 7.00004 7.96514 7.00004 8.33333M7 10.3333H7.00667M13 7C13 10.3137 10.3137 13 7 13C3.68629 13 1 10.3137 1 7C1 3.68629 3.68629 1 7 1C10.3137 1 13 3.68629 13 7Z"
                                                stroke="#6B7280"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </Tooltip>
                                </Button>
                            )}
                        </div>
                    )}
                    {intakeFormCompleted && (
                        <div className={s.intakeFormCompleted}>
                            {(intakeAssessmentNavItems.length > 0 || commonIntakeAnswers.length > 0) && (
                                <SpecificIntakeFormAssessmentBlock
                                    layout="embedded"
                                    navItems={intakeAssessmentNavItems}
                                    groupedIntakeAnswers={groupedIntakeAnswers}
                                    commonSection={
                                        commonIntakeAnswers.length > 0 ? (
                                            <div className={s.intakeCommonContent}>
                                                {commonIntakeAnswers.map((answer) => {
                                                    const answerObject: string | string[] = answer?.answer;
                                                    const isArrayAnswer = answerObject && Array.isArray(answerObject);
                                                    return (
                                                        <div className={s.intakeCommonItem} key={answer.id}>
                                                            <div className={s.intakeCommonItemQuestion}>
                                                                {answer.fullQuestionName}
                                                            </div>
                                                            <div className={s.intakeCommonItemAnswer}>
                                                                {!isArrayAnswer && <span>{answer?.answer}</span>}
                                                                {isArrayAnswer && (
                                                                    <ul>
                                                                        {(answerObject as string[]).map((listItem) => (
                                                                            <li
                                                                                className={s.intakeFormAnswerValueItem}
                                                                                key={listItem}
                                                                            >
                                                                                {listItem}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : null
                                    }
                                />
                            )}
                        </div>
                    )}
                </div>
            ),
        });
    }

    const renderClientInfo = () => (
        <>
            <div className={s.contentMid}>
                <div className={s.contentItem}>
                    <img src={UserAddIcon} className={s.contentImg} />
                    <span className={s.contentItemValue}>
                        Created: {getTimeStr(dayjs(data?.createTime), 'MMM D, YYYY [at] HH:mm')}
                    </span>
                </div>
                {/* <div className={s.contentItem}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M6.66667 5.83333V2.5M13.3333 5.83333V2.5M5.83334 9.16667H14.1667M4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V5.83333C17.5 4.91286 16.7538 4.16667 15.8333 4.16667H4.16667C3.24619 4.16667 2.5 4.91286 2.5 5.83333V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5Z" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <span className={s.contentItemValue}>Appointment: 1</span>
                </div> */}
                <div className={s.contentItem}>
                    <img src={data?.status === EStatus.Active ? CheckCircleIcon : UnCircleIcon} />
                    <span className={s.contentItemValue}>{data?.status}</span>
                </div>
                <div className={s.contentItem} style={{ paddingLeft: '4px' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
                        <path
                            d="M2.76753 13.8364C4.46056 12.8795 6.4165 12.3333 8.50001 12.3333C10.5835 12.3333 12.5395 12.8795 14.2325 13.8364M11 7.33333C11 8.71405 9.88072 9.83333 8.50001 9.83333C7.11929 9.83333 6 8.71405 6 7.33333C6 5.95262 7.11929 4.83333 8.50001 4.83333C9.88072 4.83333 11 5.95262 11 7.33333ZM16 9C16 13.1421 12.6421 16.5 8.50001 16.5C4.35787 16.5 1 13.1421 1 9C1 4.85786 4.35787 1.5 8.50001 1.5C12.6421 1.5 16 4.85786 16 9Z"
                            stroke="#6B7280"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span className={s.contentItemValue}>Patient portal: {patientPortal}</span>
                </div>
                <div className={s.contentItem} style={{ paddingLeft: '4px', alignItems: 'flex-start' }}>
                    <span className="shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 20" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M4.3575 9.08875H6.80575V11.538H9.21297V9.08875H11.6612V6.6825H9.212V4.23327H6.80575V6.6825H4.3575V9.08875ZM8.00975 0C3.65425 0 0.125 3.53025 0.125 7.88475C0.125 11.1348 1.84375 12.79 3.629 15.2013L6.64275 19.2588C6.94353 19.6416 7.29803 20 8.00995 20C8.72188 20 9.07537 19.6416 9.37715 19.2588L12.3899 15.2013C14.1751 12.7911 15.8949 11.1348 15.8949 7.88475C15.8949 3.53025 12.3657 0 8.01015 0H8.00975ZM8.00975 1.56932C11.498 1.56932 14.3263 4.39733 14.3263 7.88583C14.3263 11.3743 11.499 14.2023 8.00975 14.2023C4.5215 14.2023 1.69325 11.3743 1.69325 7.88583C1.69325 4.39733 4.5205 1.56932 8.00975 1.56932Z"
                                fill="#6B7280"
                            />
                        </svg>
                    </span>
                    <span className={s.contentItemValue}>
                        Pharmacy:{' '}
                        <UpdatedRecordPopover
                            type="pharmacy"
                            prospectChangeContext={data?.prospectChangeContext}
                            prospectData={data}
                            onRecordRead={() => {
                                onUpdate();
                            }}
                        />{' '}
                        {pharmacy
                            ? `${pharmacy.name}, ${pharmacy.address}, ${pharmacy.city}, ${pharmacy.state}, ${pharmacy.zipCode}`
                            : '-'}
                    </span>
                    {!isOps && (
                        <div className={s.sectionTitleButton} onClick={() => setPharmacyUpdateDialog({ open: true })}>
                            <img src={PencilIcon} /> Edit
                        </div>
                    )}
                </div>
            </div>
            <Tabs
                activeKey={activeKey}
                onChange={(key) => {
                    setActiveKey(key);
                }}
                items={items}
            />
        </>
    );

    const moreInfoTabs = isOps
        ? [
              {
                  label: 'Client Info',
                  key: '1',
                  children: renderClientInfo(),
              },
          ]
        : [
              {
                  label: 'Client Info',
                  key: '1',
                  children: renderClientInfo(),
              },
              {
                  label: 'Visits',
                  key: '2',
                  children: (
                      <AppointmentList
                          isInTextVisitPop={isInTextVisitPop}
                          title="Visits"
                          prospect={data}
                          forceReset={forceResetInfo}
                          smallSize
                          isGroupPractice={isGroupPractice}
                      />
                  ),
              },
              {
                  label: 'Prescriptions',
                  key: '3',
                  children: <PrescriptionsAndPharmacy prospectId={data?.id} />,
                  hide: !data?.prescribed,
              },
              {
                  label: 'Billing',
                  key: '4',
                  children: (
                      <Billings
                          clientId={isGroupPractice ? data?.identityId : data?.id}
                          isGroupPractice={isGroupPractice}
                      />
                  ),
              },
              {
                  label: 'Notes',
                  key: '5',
                  children: <InboxNotes forceReset={forceResetInfo} prospect={data} />,
              },
              {
                  label: 'Forms',
                  key: '6',
                  children: <InboxForms prospect={data} hasPlan={hasPlan} isGroupPractice={isGroupPractice} />,
              },
          ];

    const tabs = moreInfoTabs.filter((item) => !item.hide);

    return (
        <div className={`${s.wrap} ${smallSize ? s.wrapSmall : ''}`}>
            {!isSimpleType && (
                <>
                    {showChargeSuccessTip && chargeSuccessInfo && (
                        <AlertChargeSuccess
                            patientName={data?.firstName}
                            amount={chargeSuccessInfo?.amount || 0}
                            onClose={() => {
                                setShowChargeSuccessTip(false);
                                setChargeSuccessInfo(null);
                            }}
                        />
                    )}
                    <div className={s.contentTop}>
                        <div className={s.left}>
                            <h1 className={s.leftBtm}>
                                <div className={s.title}>
                                    <div className={s.titleName}>
                                        {isSample ? '[Sample]' : ''}
                                        {name}
                                        {data?.online && <span className={s.onlineIcon} />}
                                    </div>
                                    {data?.type === EProspectType.PROSPECT && (
                                        <span className={s.statusP}>
                                            Prospect
                                            {/* Client */}
                                        </span>
                                    )}
                                    {data?.type === EProspectType.PATIENT && (
                                        <span className={`${s.statusP} ${s.statusPatient}`}>Patient</span>
                                    )}
                                    {creditCardOnFile && (
                                        <span className={`${s.statusP} ${s.statusProtect}`}>
                                            {smallSize ? 'Card on file' : 'Credit card on file'}
                                        </span>
                                    )}
                                    {!creditCardOnFile && (
                                        <span className={`${s.statusP} ${s.statusUnProtect}`}>
                                            {smallSize ? 'Card pending' : 'Credit card pending'}
                                        </span>
                                    )}
                                </div>
                            </h1>
                            <div
                                style={{ marginTop: 8, cursor: 'pointer' }}
                                onClick={() => {
                                    setShowAddTags(true);
                                }}
                            >
                                <ClientTags
                                    tags={currentTagNames}
                                    maxVisible={4}
                                    enableDnd={!tagListLoading && tagList.length > 0}
                                    onMoreClick={() => {
                                        setShowAddTags(true);
                                    }}
                                    onReorder={(next) => {
                                        handleReorderTags(next);
                                    }}
                                    onTagAction={(tag) => {
                                        handleRemoveTag(tag);
                                    }}
                                    tagActionText="Remove tag"
                                    onAddClick={() => setShowAddTags(true)}
                                />
                            </div>
                        </div>
                    </div>
                    {data?.boundProviders && data.boundProviders.length > 0 && (
                        <div className={s.assignedProviders}>
                            <div className={s.assignedProvidersLabel}>Assigned Providers</div>
                            {data.boundProviders.map((bp) => {
                                const initials = `${bp.firstName?.[0] || ''}${bp.lastName?.[0] || ''}`.toUpperCase();
                                // eligibility 开着时这张卡是「这次 check 以谁的身份发出」的答案：
                                // 选中它，整个模块（结果卡、标题行 tag、History、保险资料区、
                                // 计费身份）都切到那位 provider 名下（原型 09/15 Assigned providers）
                                const elig = eligApi.enabled
                                    ? eligApi.providers.find((p) => p.providerId === bp.providerId)
                                    : undefined;
                                const selected = !!elig && eligApi.selectedProviderId === bp.providerId;
                                return (
                                    <div
                                        key={bp.providerId}
                                        className={`${s.assignedProviderItem} ${elig ? s.assignedProviderPick : ''} ${
                                            selected ? s.assignedProviderOn : ''
                                        }`}
                                        onClick={() => {
                                            if (elig) eligApi.selectProvider(elig.providerId);
                                        }}
                                    >
                                        {bp.avatar ? (
                                            <img
                                                src={bp.avatar}
                                                alt={initials}
                                                className={s.assignedProviderAvatar}
                                                style={{ background: 'none' }}
                                            />
                                        ) : (
                                            <div className={s.assignedProviderAvatar}>{initials}</div>
                                        )}
                                        <div className={s.assignedProviderInfo}>
                                            <div className={s.assignedProviderName}>
                                                {bp.firstName} {bp.lastName}
                                                {elig?.self && <span className={s.assignedProviderYou}>You</span>}
                                            </div>
                                            {bp.credential && (
                                                <div className={s.assignedProviderCred}>{bp.credential}</div>
                                            )}
                                        </div>
                                        {/* 每张卡自带这位 provider 自己的状态：谁还没查，不点进去也看得到 */}
                                        {elig && (
                                            <span className={s.assignedProviderTag}>
                                                <EligibilityStatusTag
                                                    status={elig.status}
                                                    tipOverride={elig.tipOverride}
                                                />
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {showRelateProvider && (
                        <div className={s.rProvider}>
                            <Popover
                                content={
                                    <div className={s.assoContent}>
                                        Associated providers:{' '}
                                        {providerList.map((item, inx) => {
                                            const isLast = inx === providerList.length - 1;
                                            return (
                                                <span
                                                    onClick={() => {
                                                        //http://localhost:8085/operation/uniprofile-detail/-1/1695
                                                        window.open(
                                                            `/operation/uniprofile-detail/-1/${item.providerId}`,
                                                            '_blank',
                                                        );
                                                    }}
                                                    key={item.providerId}
                                                    className={s.assoItem}
                                                >
                                                    {item.providerName}
                                                    {!isLast && ',  '}
                                                </span>
                                            );
                                        })}
                                    </div>
                                }
                            >
                                <p className={s.asso}>Associated providers: {providersText}</p>
                            </Popover>
                        </div>
                    )}
                    {!hideCharge && !isOps && !isGroupPractice && (
                        <Dropdown
                            menu={{
                                items: isPPAProvider
                                    ? [
                                          {
                                              key: 'charge-now',
                                              label: (
                                                  <div>
                                                      {creditCardOnFile ? (
                                                          <Popover
                                                              color="#F0FCF6"
                                                              content={
                                                                  <div className={s.chargeClientBody}>
                                                                      Easily collect fees for no-shows, late
                                                                      cancellations, co-pays, and more. Charges will be
                                                                      processed automatically if the client has a card
                                                                      on file, or sent as an invoice if not.
                                                                  </div>
                                                              }
                                                              title={
                                                                  <div className={s.chargeClientHeader}>
                                                                      <div className={s.tag}>New</div>
                                                                      <div className={s.title}>
                                                                          You can now charge your client!
                                                                      </div>
                                                                      <CloseOutlined
                                                                          onClick={closeChargeClinetPopover}
                                                                      />
                                                                  </div>
                                                              }
                                                              // trigger="click"
                                                              open={
                                                                  showChargeClientPopover &&
                                                                  !localStorage.getItem(DISABLE_CHARGE_PATIENTS_POPUP)
                                                              }
                                                          >
                                                              <div
                                                                  className="my-1"
                                                                  onClick={() => {
                                                                      setShowChargeClientModal(true);
                                                                      closeChargeClinetPopover();
                                                                  }}
                                                              >
                                                                  Charge now
                                                              </div>
                                                          </Popover>
                                                      ) : (
                                                          <Tooltip title="You cannot charge this client because they do not have a payment method on file.">
                                                              <div className={s.disabledChargeBtn}>Charge now</div>
                                                          </Tooltip>
                                                      )}
                                                  </div>
                                              ),
                                          },
                                          {
                                              key: 'send-invoice',
                                              label: (
                                                  <div className="my-1 flex items-center gap-2">
                                                      <span>Send invoice</span>
                                                      <span className={s.newTag}>New</span>
                                                  </div>
                                              ),
                                              onClick: () => {
                                                  setShowSendInvoiceModal(true);
                                                  closeChargeClinetPopover();
                                              },
                                          },
                                      ]
                                    : [
                                          {
                                              key: 'charge-now',
                                              label: (
                                                  <div>
                                                      {creditCardOnFile ? (
                                                          <Popover
                                                              color="#F0FCF6"
                                                              content={
                                                                  <div className={s.chargeClientBody}>
                                                                      Easily collect fees for no-shows, late
                                                                      cancellations, co-pays, and more. Charges will be
                                                                      processed automatically if the client has a card
                                                                      on file, or sent as an invoice if not.
                                                                  </div>
                                                              }
                                                              title={
                                                                  <div className={s.chargeClientHeader}>
                                                                      <div className={s.tag}>New</div>
                                                                      <div className={s.title}>
                                                                          You can now charge your client!
                                                                      </div>
                                                                      <CloseOutlined
                                                                          onClick={closeChargeClinetPopover}
                                                                      />
                                                                  </div>
                                                              }
                                                              // trigger="click"
                                                              open={
                                                                  showChargeClientPopover &&
                                                                  !localStorage.getItem(DISABLE_CHARGE_PATIENTS_POPUP)
                                                              }
                                                          >
                                                              <div
                                                                  className="my-1"
                                                                  onClick={() => {
                                                                      setShowChargeClientModal(true);
                                                                      closeChargeClinetPopover();
                                                                  }}
                                                              >
                                                                  Charge now
                                                              </div>
                                                          </Popover>
                                                      ) : (
                                                          <Tooltip title="You cannot charge this client because they do not have a payment method on file.">
                                                              <div className={s.disabledChargeBtn}>Charge now</div>
                                                          </Tooltip>
                                                      )}
                                                  </div>
                                              ),
                                          },
                                          {
                                              key: 'send-invoice',
                                              label: (
                                                  <div className="my-1 flex items-center gap-2">
                                                      <span>Send invoice</span>
                                                      <span className={s.newTag}>New</span>
                                                  </div>
                                              ),
                                              onClick: () => {
                                                  setShowSendInvoiceModal(true);
                                                  closeChargeClinetPopover();
                                              },
                                          },
                                      ],
                            }}
                            trigger={['click']}
                            className={s.chargeDropdown}
                        >
                            <Button>
                                Charge client <DownOutlined />
                            </Button>
                        </Dropdown>
                    )}
                </>
            )}
            {showMoreInfo ? (
                <div className={s.moreInfo}>
                    <div className={s.moreInfoTabs}>
                        {tabs.map((item) => (
                            <div
                                key={item.key}
                                className={`${s.moreInfoTab} ${moreInfoTab === item.key ? s.moreInfoTabActive : ''}`}
                                onClick={() => setMoreInfoTab(item.key)}
                            >
                                {item.label}
                            </div>
                        ))}
                    </div>
                    {tabs.find((item) => item.key === moreInfoTab)?.children}
                </div>
            ) : (
                renderClientInfo()
            )}
            <ChargeModal
                connectAccountInfo={connectAccountInfo}
                providerId={data?.providerId}
                clientId={data?.id}
                billingPolicy={billingPolicy?.sort((a, b) => a.id - b.id)}
                open={showChargeClientModal}
                onCancel={() => setShowChargeClientModal(false)}
                onSuccess={(amount) => {
                    setShowChargeClientModal(false);
                    setChargeSuccessInfo({ name: data?.firstName || '', amount });
                    setShowChargeSuccessTip(true);
                }}
            />
            <InvoiceModal
                open={showSendInvoiceModal}
                onCancel={() => setShowSendInvoiceModal(false)}
                onSuccess={() => {
                    setShowSendInvoiceModal(false);
                    onUpdate();
                    setDataUpdatedFlag('billingDataUpdated', {});
                }}
                patientName={name.trim() || 'Patient'}
                clientId={data?.id}
                state={data?.state}
            />
            <AddClientTagsModal
                open={showAddTags}
                clientName={name.trim() || 'Client'}
                existingTags={currentTagNames}
                onCancel={() => setShowAddTags(false)}
                onSubmit={async (next) => {
                    await handleAddTags(next);
                    setShowAddTags(false);
                }}
                isGroupPractice={isGroupPractice}
                identityId={data?.identityId}
            />
            <InsurancePopup
                open={!!showInsurancePopup}
                initialTab={showInsurancePopup || 'PRIMARY'}
                onClose={() => setShowInsurancePopup(false)}
                onEdit={() => {
                    setShowInsurancePopup(false);
                    setShowEditInsurance(true);
                }}
                insurances={shownInsurances || []}
                onViewImage={(url) => setViewImgUrl(url)}
                // eligibility 模块开着才报上次答案（ops / 复用点不带）
                checkCards={eligApi.enabled ? eligApi.checkCards : undefined}
            />
            <EditInsuranceModal
                open={showEditInsurance}
                onClose={() => setShowEditInsurance(false)}
                onSuccess={() => {
                    setShowEditInsurance(false);
                    onUpdate();
                }}
                item={data}
                isGroupPractice={isGroupPractice}
            />
            {showEdit && (editType === 1 || editType === 2) && (
                <AddAndUpdateProspects
                    item={data}
                    show={showEdit}
                    isGroupPractice={isGroupPractice}
                    onSuccess={() => {
                        onUpdate();
                        setShowEdit(false);
                    }}
                    onCannel={() => {
                        setShowEdit(false);
                    }}
                    type={editType as 1 | 2}
                    onClickEmptyState={() => {
                        setEditType(1);
                        setShowEdit(true);
                    }}
                />
            )}
            <Modal
                open={!!viewImgUrl}
                width={600}
                onCancel={() => {
                    setViewImgUrl(undefined);
                }}
                onOk={() => {
                    setViewImgUrl(undefined);
                }}
                footer={null}
            >
                <img className={s.viewImgModal} src={viewImgUrl} />
            </Modal>

            <PatientHealthInfoUpdateDialog
                prospectId={isGroupPractice ? data?.identityId : data?.id}
                conditionHealth={data?.conditionHealth}
                medicationHealth={data?.medicationHealth}
                allergyHealth={data?.allergyHealth}
                open={patientUpdateDialog.open}
                type={patientUpdateDialog.type as PatientUpdateType}
                onCancel={() => setPatientUpdateDialog({ ...initPatientUpdateDialog })}
                onUpdated={onUpdate}
                isGroupPractice={isGroupPractice}
            />
            <PharmacySelectDialog
                prospectId={isGroupPractice ? data?.identityId : data?.id}
                pharmacy={pharmacy ? [pharmacy] : []}
                open={pharmacyUpdateDialog.open}
                onCancel={() => setPharmacyUpdateDialog({ ...initPharmacyUpdateDialog })}
                onUpdated={onUpdate}
                isGroupPractice={isGroupPractice}
            />
        </div>
    );
};

export default ProsepectInfo;
